import { Fragment, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, GraduationCap, Trash2, ShieldCheck, Clock, Tag, X } from 'lucide-react';
import PurchaseService from '~/services/purchase.service';
import CouponService from '~/services/coupon.service';
import { useCart } from '~/context/purchase';
import { useNavigate } from 'react-router';
import { formatDuration } from '~/helpers/formatDuration';
import Modal from '~/components/modal';
import { LoadingButton } from './loading-button';
import { useAuth } from '~/context/auth';
import type { CouponValidateResponse } from '~/types';

const MIN_PURCHASE_TOTAL_PER_COURSE_CENTS = 1;

const currency = (cents?: number) =>
  typeof cents === 'number'
    ? (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    : '—';

export default function CartModal() {
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidateResponse | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const { items, isModalOpen, closeCart, removeFromCart, clearCart } = useCart();
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();

  const totalCents = useMemo(
    () =>
      items.reduce((acc, it) => {
        const price = typeof it.course.price === 'number' ? it.course.price : it.course.price_cents;
        return acc + (typeof price === 'number' ? price : 0);
      }, 0),
    [items],
  );
  const minimumPurchaseTotalCents = items.length * MIN_PURCHASE_TOTAL_PER_COURSE_CENTS;

  const rawDisplayTotalCents =
    appliedCoupon?.valid && typeof appliedCoupon.final_total_cents === 'number'
      ? appliedCoupon.final_total_cents
      : totalCents;
  const shouldShowMinimumPurchaseNotice =
    appliedCoupon?.valid && rawDisplayTotalCents <= minimumPurchaseTotalCents;
  const displayTotalCents = shouldShowMinimumPurchaseNotice
    ? minimumPurchaseTotalCents
    : rawDisplayTotalCents;
  const rawDiscountCents =
    appliedCoupon?.valid && typeof appliedCoupon.discount_amount_cents === 'number'
      ? appliedCoupon.discount_amount_cents
      : 0;
  const discountCents = shouldShowMinimumPurchaseNotice
    ? Math.max(totalCents - minimumPurchaseTotalCents, 0)
    : rawDiscountCents;

  useEffect(() => {
    setAppliedCoupon(null);
    setCouponError(null);
  }, [totalCents]);

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code || isApplyingCoupon || totalCents <= 0) return;
    if (!isAuthenticated) {
      setCouponError('Faça login para aplicar o cupom');
      return;
    }
    setCouponError(null);
    try {
      setIsApplyingCoupon(true);
      const result = await CouponService.validate({ code, cart_total_cents: totalCents });
      if (result.valid) {
        setAppliedCoupon(result);
        setCouponError(null);
      } else {
        setAppliedCoupon(null);
        setCouponError(result.message ?? 'Cupom inválido');
      }
    } catch {
      setAppliedCoupon(null);
      setCouponError('Não foi possível validar o cupom');
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError(null);
    setCouponCode('');
  };

  if (!isModalOpen) return null;

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      login();
      closeCart();
      return;
    }

    if (isCheckingOut || items.length === 0) return;

    try {
      setIsCheckingOut(true);
      const couponToSend = appliedCoupon?.valid ? appliedCoupon.coupon?.code : undefined;
      const response = await PurchaseService.createPurchase(
        items.map((i) => i.course.id),
        couponToSend,
      );
      window.open(response.paymentLink.url, '_blank');
      clearCart();
      closeCart();
      handleRemoveCoupon();
    } catch (e) {
      console.error('Erro ao finalizar compra:', e);
    } finally {
      setIsCheckingOut(false);
    }
  };

  const myCarTextClass =
    items.length > 0
      ? 'text-base font-medium text-content-primary mx-auto'
      : 'text-[22px] leading-[26px] font-semibold text-content-primary tracking-[-0.44px]';

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={closeCart}
      title={<h2 className={myCarTextClass}>Seu carrinho</h2>}
      maxWidth="max-w-[680px]"
    >
      {items.length === 0 ? (
        <div className="mx-auto flex h-56 sm:h-[268px] w-full max-w-[95vw] sm:max-w-[520px] flex-col items-center justify-center gap-2 rounded-xl pt-3.5 pr-3.5 pb-6 pl-3.5 text-center">
          <GraduationCap size={44} className="mb-2 text-bg-core-dark sm:w-16 sm:h-16" />

          <h3 className="text-base font-medium leading-5 text-text-dark">
            Seu carrinho está vazio
          </h3>

          <p className="text-sm sm:text-[16px] font-normal leading-6 text-text-dark">
            Explore a biblioteca e adicione seu primeiro curso.
          </p>

          <button
            onClick={() => {
              closeCart();
              navigate('/');
            }}
            className="mt-5 flex h-11 sm:h-12 w-full sm:w-[480px] min-w-[72px] items-center justify-center gap-2 rounded-xl bg-bg-core-dark text-sm font-normal leading-5 text-white cursor-pointer hover:brightness-90 transition-all"
          >
            Explorar cursos
          </button>
        </div>
      ) : (
        <Fragment>
          <div className="max-h-[52vh] overflow-y-auto pt-1">
            <ul className="divide-y divide-slate-200">
              {items.map((it) => {
                const c = it.course;
                return (
                  <li key={it.course.id} className="flex items-center gap-4 sm:gap-6 py-4">
                    <img
                      src={c.thumbnail_url}
                      alt={it.course.title}
                      className="h-16 w-16 sm:h-32 sm:w-32 shrink-0 rounded-[20px] object-cover"
                    />

                    <div className="min-w-0 flex-1">
                      <p className="text-sm sm:text-base font-semibold text-text-dark">
                        {it.course.title}
                      </p>
                      <p className="mt-2 line-clamp-2 text-sm leading-4.5 text-text-secondary hidden sm:block">
                        {it.course.description}
                      </p>

                      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm leading-4.5 text-text-tertiary">
                        <span>{c.lessons_count ?? 0} atividades</span>
                        <span>•</span>

                        <div className="flex items-center gap-1">
                          <Clock size={16} strokeWidth={1.5} className="text-icon-tertiary" />
                          <span>{formatDuration(c.duration_minutes)}</span>
                        </div>

                        {c.category?.name ? (
                          <>
                            <span>•</span>
                            <span>{c.category.name}</span>
                          </>
                        ) : null}
                      </div>

                      <p className="mt-3 text-base font-medium text-text-dark">
                        {currency(typeof c.price === 'number' ? c.price : c.price_cents)}
                      </p>
                    </div>

                    <button
                      onClick={() => removeFromCart(it.course.id)}
                      className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-bg-quaternary p-2 self-center hover:brightness-90 cursor-pointer transition-all"
                      aria-label="Remover item"
                      title="Remover"
                    >
                      <Trash2 size={20} strokeWidth={1.5} className="text-text-dark" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="rounded-b-[36px] border-t border-border-tertiary bg-white">
            <div className="border-t border-border-tertiary px-4 sm:px-6 py-4">
              <p className="text-sm font-medium text-text-dark mb-2">Cupom de desconto</p>
              {appliedCoupon?.valid ? (
                <div className="flex items-center justify-between gap-2 rounded-lg bg-bg-secondary px-3 py-2">
                  <span className="flex items-center gap-2 text-sm font-medium text-text-dark">
                    <Tag size={16} className="text-bg-core-dark" />
                    {appliedCoupon.coupon?.code ?? couponCode}
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveCoupon}
                    className="flex items-center gap-1 text-xs text-text-secondary hover:text-text-dark transition-colors"
                  >
                    <X size={14} /> Remover
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => {
                      setCouponCode(e.target.value.toUpperCase());
                      setCouponError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApplyCoupon();
                      }
                    }}
                    placeholder="Código do cupom"
                    className="flex-1 min-w-0 px-3 py-2 border border-border-tertiary rounded-lg text-sm text-text-dark placeholder:text-text-secondary focus:outline-none focus:ring-2 focus:ring-bg-core-dark/20 focus:border-bg-core-dark"
                  />
                  <LoadingButton
                    type="button"
                    onClick={handleApplyCoupon}
                    isLoading={isApplyingCoupon}
                    loadingText=""
                    disabled={!couponCode.trim() || isApplyingCoupon}
                    className="shrink-0 px-4 py-2 rounded-lg bg-bg-core-dark text-sm font-medium text-white hover:brightness-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Aplicar
                  </LoadingButton>
                </div>
              )}
              {couponError && <p className="mt-1.5 text-xs text-red-600">{couponError}</p>}
            </div>

            <div className="flex flex-col gap-2 px-4 sm:px-6">
              {appliedCoupon?.valid && discountCents > 0 && (
                <>
                  <div className="flex justify-between text-sm text-text-secondary">
                    <span>Subtotal</span>
                    <span>{currency(totalCents)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-text-secondary">
                    <span>Desconto ({appliedCoupon.coupon?.code ?? 'cupom'})</span>
                    <span className="text-palette-success">-{currency(discountCents)}</span>
                  </div>
                </>
              )}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <span className="text-[18px] font-medium leading-6 tracking-[-0.18px] text-text-dark">
                  Total
                </span>
                <span className="text-[18px] font-medium leading-6 tracking-[-0.18px] text-text-dark">
                  {currency(displayTotalCents)}
                </span>
              </div>
              {shouldShowMinimumPurchaseNotice && (
                <div className="w-full">
                  <p className="flex w-full items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left text-xs text-amber-800">
                    <AlertTriangle size={14} />
                    <span>
                      O valor mínimo por curso é de {currency(MIN_PURCHASE_TOTAL_PER_COURSE_CENTS)}.
                    </span>
                  </p>
                </div>
              )}
            </div>

            <div className="p-5">
              <LoadingButton
                onClick={handleCheckout}
                isLoading={isCheckingOut}
                loadingText="Finalizando..."
                className="flex h-12 w-full min-w-[72px] items-center justify-center gap-2 rounded-xl bg-bg-core-dark px-3 text-[14px] font-normal leading-5 text-white mx-auto cursor-pointer hover:brightness-90 transition-all"
              >
                Finalizar compra
              </LoadingButton>

              <div className="mt-[13px] flex w-full items-center justify-center gap-2 text-center">
                <ShieldCheck size={20} strokeWidth={1.5} className="text-icon-success" />
                <span className="text-[14px] font-normal leading-[18px] text-text-secondary">
                  Pagamento seguro via Pagar.me. Acesso imediato após confirmação.
                </span>
              </div>
            </div>
          </div>
        </Fragment>
      )}
    </Modal>
  );
}
